import React from 'react';
import { Link } from 'react-router-dom';
import './Auth.css';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="auth-container">
      <div className="auth-card" style={{ maxWidth: '800px', textAlign: 'left' }}>
        <div className="auth-header">
          <h1>🦌 culr.app</h1>
          <h2>Privacy Policy</h2>
        </div>

        <div style={{ padding: '20px 0', lineHeight: '1.6' }}>
          <p><strong>Last updated:</strong> {new Date().toLocaleDateString()}</p>
          <p><strong>Effective Date:</strong> {new Date().toLocaleDateString()}</p>

          <h3>1. Data Controller Information</h3>
          <p>
            <strong>Data Controller:</strong> culr.app<br/>
            <strong>Contact:</strong> [contact email]<br/>
            <strong>Data Protection Officer:</strong> [dpo email]<br/>
            <strong>Address:</strong> [UK business address]
          </p>

          <h3>2. What Data We Collect</h3>
          <h4>Personal Data:</h4>
          <ul>
            <li><strong>Google Account Information:</strong> Name, email address, profile picture (via Google OAuth)</li>
            <li><strong>Account Data:</strong> User ID, account creation date, login timestamps</li>
            <li><strong>Usage Data:</strong> IP address, browser type, device information, pages visited</li>
          </ul>

          <h4>Cull Tracking Data:</h4>
          <ul>
            <li>Date, time, and location of deer cull activities</li>
            <li>Species, weight, condition, and shot details</li>
            <li>Environmental conditions and weather data</li>
            <li>Equipment used and disposal methods</li>
            <li>User comments and observations</li>
          </ul>

          <h3>3. Legal Basis for Processing</h3>
          <p>We process your personal data under the following legal bases:</p>
          <ul>
            <li><strong>Legitimate Interests:</strong> Wildlife management, conservation efforts, and service provision</li>
            <li><strong>Consent:</strong> Optional analytics and marketing communications</li>
            <li><strong>Legal Obligation:</strong> Compliance with wildlife protection laws and regulations</li>
            <li><strong>Contract:</strong> Account management and service delivery</li>
          </ul>

          <h3>4. How We Use Your Data</h3>
          <p>We use your personal data to:</p>
          <ul>
            <li>Provide and maintain the deer cull tracking service</li>
            <li>Authenticate users and secure accounts</li>
            <li>Generate reports and analytics for conservation purposes</li>
            <li>Comply with wildlife management regulations</li>
            <li>Improve our service and user experience</li>
            <li>Communicate important service updates</li>
          </ul>

          <h3>5. Data Sharing and Disclosure</h3>
          <p>We may share your data with:</p>
          <ul>
            <li><strong>Government Agencies:</strong> Wildlife authorities for regulatory compliance</li>
            <li><strong>Conservation Organizations:</strong> Aggregated, anonymized data for research</li>
            <li><strong>Service Providers:</strong> Google (authentication), hosting providers (data storage)</li>
            <li><strong>Legal Requirements:</strong> When required by law or court order</li>
          </ul>
          <p>We do not sell your personal data to third parties.</p>

          <h3>6. International Data Transfers</h3>
          <p>
            Your data may be processed outside the UK by our service providers (Google, cloud hosting). 
            All transfers are protected by appropriate safeguards including adequacy decisions and 
            standard contractual clauses.
          </p>

          <h3>7. Data Retention</h3>
          <p>We retain your data for the following periods:</p>
          <ul>
            <li><strong>Account Data:</strong> Until account deletion or 2 years after last login</li>
            <li><strong>Cull Records:</strong> 7 years (for regulatory compliance)</li>
            <li><strong>Usage Logs:</strong> 12 months</li>
            <li><strong>Cookie Data:</strong> As specified in cookie settings</li>
          </ul>

          <h3>8. Your Data Protection Rights (UK GDPR)</h3>
          <p>You have the following rights regarding your personal data:</p>
          
          <h4>Right of Access (Article 15)</h4>
          <p>Request copies of your personal data and information about how it's processed.</p>

          <h4>Right to Rectification (Article 16)</h4>
          <p>Request correction of inaccurate or incomplete personal data.</p>

          <h4>Right to Erasure (Article 17)</h4>
          <p>Request deletion of your personal data in certain circumstances.</p>

          <h4>Right to Restrict Processing (Article 18)</h4>
          <p>Request limitation of processing in specific situations.</p>

          <h4>Right to Data Portability (Article 20)</h4>
          <p>Request transfer of your data to another service provider.</p>

          <h4>Right to Object (Article 21)</h4>
          <p>Object to processing based on legitimate interests or for direct marketing.</p>

          <h4>Rights Related to Automated Decision Making</h4>
          <p>We do not use automated decision-making or profiling that significantly affects you.</p>

          <h3>9. How to Exercise Your Rights</h3>
          <p>To exercise your data protection rights:</p>
          <ul>
            <li>Email us at [dpo email] with your request</li>
            <li>Include proof of identity (for security)</li>
            <li>Specify which right you wish to exercise</li>
            <li>We will respond within one month</li>
          </ul>

          <h3>10. Cookies and Tracking</h3>
          <h4>Essential Cookies:</h4>
          <ul>
            <li>Authentication tokens (required for login)</li>
            <li>Session management (required for functionality)</li>
            <li>Security cookies (CSRF protection)</li>
          </ul>

          <h4>Optional Cookies (require consent):</h4>
          <ul>
            <li>Google Analytics (usage statistics)</li>
            <li>Performance monitoring</li>
            <li>User preferences</li>
          </ul>

          <h3>11. Data Security</h3>
          <p>We implement appropriate technical and organizational measures to protect your data:</p>
          <ul>
            <li>Encryption in transit and at rest</li>
            <li>Access controls and authentication</li>
            <li>Regular security assessments</li>
            <li>Staff training on data protection</li>
            <li>Incident response procedures</li>
          </ul>

          <h3>12. Children's Privacy</h3>
          <p>
            Our service is not intended for children under 16. We do not knowingly collect personal data 
            from children under 16. If you believe we have collected such data, please contact us immediately.
          </p>

          <h3>13. Data Breach Notification</h3>
          <p>
            In the event of a data breach that may pose a risk to your rights and freedoms, we will:
          </p>
          <ul>
            <li>Notify the ICO within 72 hours</li>
            <li>Inform affected users without undue delay</li>
            <li>Provide details of the breach and mitigation steps</li>
          </ul>

          <h3>14. Updates to This Policy</h3>
          <p>
            We may update this Privacy Policy periodically. Significant changes will be communicated via:
          </p>
          <ul>
            <li>Email notification to registered users</li>
            <li>Prominent notice on our website</li>
            <li>In-app notifications</li>
          </ul>

          <h3>15. Complaints and Supervisory Authority</h3>
          <p>
            If you have concerns about how we handle your personal data, you can:
          </p>
          <ul>
            <li>Contact us directly at [dpo email]</li>
            <li>File a complaint with the Information Commissioner's Office (ICO)</li>
          </ul>
          <p>
            <strong>ICO Contact:</strong><br/>
            Website: ico.org.uk<br/>
            Phone: 0303 123 1113<br/>
            Post: Information Commissioner's Office, Wycliffe House, Water Lane, Wilmslow, Cheshire, SK9 5AF
          </p>

          <h3>16. Contact Us</h3>
          <p>For privacy-related questions or to exercise your rights:</p>
          <p>
            <strong>Email:</strong> [dpo email]<br/>
            <strong>General Contact:</strong> [contact email]<br/>
            <strong>Address:</strong> [UK business address]
          </p>
        </div>

        <div className="auth-footer">
          <p>
            <Link to="/">Back to Home</Link> | <Link to="/terms">Terms & Conditions</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;